import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import DateStringInput from '@repo/ui/date-string-input'
import { GlobeIcon, ResetTwoIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import MultiSelectInput from '@repo/ui/multiselect-input'
import RadioInput from '@repo/ui/radio-input'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { addDays } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'

import { getCountries } from '../../../../api/countries/get-countries'
import { getOtoIndividualProducts } from '../../../../api/products/get-oto-individual-products'
import {
  OtoIndividualProductRow,
  OtoIndividualProductsFilters
} from '../../../../lib/types/models/product/product-oto-individual'
import { filterDuplicates } from '../../../../lib/utils/functions/filter-duplicates'
import { childAges } from '../../../../views/pcr-personal-space/utils'
import CountryFlag from '../../../elements/country-flag'
import {
  getOtoIndividualProducts as getLoadedProducts,
  getOtoIndividualReturnDate,
  getOtoIndividualStayPrice,
  getOtoIndividualWeeksLabel,
  getRegionOfStayLabel,
  OTO_INDIVIDUAL_NUMBER_OF_HOURS,
  OTO_INDIVIDUAL_NUMBER_OF_WEEKS,
  OtoIndividualRow,
  OtoIndividualRowDraft
} from '../utils/oto-individual-utils'

type Props = {
  selectedRowId?: string
  drafts: Record<string, OtoIndividualRowDraft>
  onDraftChange: (rowId: string, draft: OtoIndividualRowDraft) => void
  onSelectRow: (row: OtoIndividualRow) => void
  onSubmit: () => void
}

const OtoIndividualProductListSelection = ({
  selectedRowId,
  drafts,
  onDraftChange,
  onSelectRow,
  onSubmit
}: Props) => {
  const [filter, setFilter] = useState<OtoIndividualProductsFilters>({})

  const {
    data: rows,
    isPending: isProductsPending,
    isError: isProductsError
  } = useQuery({
    queryKey: ['oto-individual-products', filter],
    queryFn: () => getOtoIndividualProducts(filter)
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  // a row seeded from an existing proposition only carries the saved région: re-emit it from
  // the loaded rows as soon as they arrive so every région of the product is selectable
  useEffect(() => {
    const row = rows?.find((record) => record.rowId === selectedRowId)
    if (row) onSelectRow(row)
  }, [selectedRowId, rows])

  // the regions (journey places) and arrival places of the loaded products, for the filters
  const regionOptions = useMemo(() => {
    const places = getLoadedProducts(rows ?? []).flatMap((product) =>
      product.journeyPlaces.map((journeyPlace) => journeyPlace.place)
    )
    return filterDuplicates(places, (place) => place.id)
  }, [rows])

  const arrivalPlaceOptions = useMemo(() => {
    const places = regionOptions
      .filter((place) => !filter.journeyPlaceId || place.id === filter.journeyPlaceId)
      .flatMap((place) => place.arrivalPoint)
    return filterDuplicates(places, (place) => place.id)
  }, [regionOptions, filter.journeyPlaceId])

  const getDraft = (row: OtoIndividualProductRow): OtoIndividualRowDraft => drafts[row.rowId] ?? {}

  const columns: Column<OtoIndividualProductRow>[] = [
    {
      accessorKey: 'rowId',
      header: '',
      cell: ({ row: { original: record } }) => (
        <RadioInput checked={selectedRowId === record.rowId} onCheck={() => onSelectRow(record)} />
      )
    },
    {
      accessorKey: 'product.country.name',
      header: 'Pays',
      centerCell: true,
      cellClassName: 'px-0',
      cell: ({ row: { original: record } }) => <CountryFlag code={record.product.country.code} />
    },
    {
      accessorKey: 'numberOfWeeks',
      header: 'Durée en semaines',
      width: '110px',
      cell: ({ row: { original: record } }) => (
        <div className="flex flex-col py-2 gap-1">
          <Typography variant="secondary-body">{record.weeksTitle}</Typography>
          <Typography variant="label" color={tailwindColors.grey.DEFAULT}>
            {record.nightsLabel}
          </Typography>
        </div>
      )
    },
    {
      accessorKey: 'rowId',
      header: 'Date de départ',
      width: '165px',
      cell: ({ row: { original: record } }) => (
        <DateStringInput
          label="Date de départ"
          mode="over"
          disabledPastDates
          // departures are always on a Sunday
          disabledDaysOfWeek={[1, 2, 3, 4, 5, 6]}
          value={getDraft(record).departureDate}
          onChange={(date) =>
            onDraftChange(record.rowId, {
              ...getDraft(record),
              departureDate: date ?? undefined,
              // a new departure date auto-fills the date de retour again
              returnDate: date
                ? DateFormatter.toDateFormatted(
                    getOtoIndividualReturnDate(DateFormatter.toDate(date), record.numberOfWeeks)
                  )
                : undefined
            })
          }
        />
      )
    },
    {
      accessorKey: 'rowId',
      header: 'Date de retour',
      width: '165px',
      cell: ({ row: { original: record } }) => {
        const { departureDate, returnDate } = getDraft(record)
        return (
          <DateStringInput
            label="Date de retour"
            mode="over"
            disabled={!departureDate}
            startDate={departureDate ? addDays(DateFormatter.toDate(departureDate), 1) : undefined}
            value={returnDate}
            onChange={(date) =>
              onDraftChange(record.rowId, { ...getDraft(record), returnDate: date ?? undefined })
            }
          />
        )
      }
    },
    {
      accessorKey: 'rowId',
      header: '',
      headerInfo: (
        <div className="flex flex-col">
          <span>Cours particuliers</span>
          <span className="font-normal text-grey">{"nb d'heures/ semaine"}</span>
        </div>
      ),
      width: '135px',
      cell: ({ row: { original: record } }) => (
        <SelectInput<number>
          label="Heures"
          options={OTO_INDIVIDUAL_NUMBER_OF_HOURS.map((hours) => ({
            label: `${hours}h`,
            value: hours
          }))}
          selectedOption={conditionalRender(!!getDraft(record).numberOfHours, {
            label: `${getDraft(record).numberOfHours}h`,
            value: getDraft(record).numberOfHours!
          })}
          onChange={(numberOfHours) =>
            onDraftChange(record.rowId, { ...getDraft(record), numberOfHours })
          }
        />
      )
    },
    {
      accessorKey: 'rowId',
      header: 'Région de séjour',
      width: '270px',
      cell: ({ row: { original: record } }) => {
        const draft = getDraft(record)
        const selectedRegion = record.product.journeyPlaces.find(
          (journeyPlace) => journeyPlace.id === draft.regionOfStayId
        )
        return (
          <SelectInput<number>
            label="Région"
            options={record.product.journeyPlaces.map((journeyPlace) => ({
              label: getRegionOfStayLabel(journeyPlace),
              value: journeyPlace.id
            }))}
            selectedOption={conditionalRender(!!draft.regionOfStayId, {
              label: selectedRegion ? getRegionOfStayLabel(selectedRegion) : '',
              value: draft.regionOfStayId!
            })}
            onChange={(regionOfStayId) =>
              // changing the region resets the arrival place; re-picking the same one keeps it
              onDraftChange(record.rowId, {
                ...draft,
                regionOfStayId,
                arrivalPlaceId:
                  regionOfStayId === draft.regionOfStayId ? draft.arrivalPlaceId : undefined
              })
            }
          />
        )
      }
    },
    {
      accessorKey: 'rowId',
      header: "Lieu d'arrivée",
      width: '270px',
      cell: ({ row: { original: record } }) => {
        const draft = getDraft(record)
        const region = record.product.journeyPlaces.find((jp) => jp.id === draft.regionOfStayId)
        const arrivalPlaces = region?.place.arrivalPoint ?? []
        return (
          <SelectInput<number>
            label="Choisir"
            disabled={!region}
            options={arrivalPlaces.map((place) => ({ label: place.title, value: place.id }))}
            selectedOption={conditionalRender(!!draft.arrivalPlaceId, {
              label: arrivalPlaces.find((place) => place.id === draft.arrivalPlaceId)?.title ?? '',
              value: draft.arrivalPlaceId!
            })}
            onChange={(arrivalPlaceId) =>
              onDraftChange(record.rowId, { ...draft, arrivalPlaceId })
            }
          />
        )
      }
    },
    {
      accessorKey: 'rowId',
      header: '',
      headerInfo: (
        <div className="flex flex-col">
          <span>Frais de séjour</span>
          <span className="font-normal text-grey">Frais de séjour (hors voyage)</span>
        </div>
      ),
      width: '190px',
      cell: ({ row: { original: record } }) => {
        // the price is only known once a région du séjour is selected for the row
        const stayPrice = getOtoIndividualStayPrice(record, getDraft(record).regionOfStayId)
        if (stayPrice == null) return '-'
        return (
          <div className="flex flex-row items-center gap-1 whitespace-nowrap">
            <Typography variant="label" color={tailwindColors.grey.DEFAULT}>
              à partir de
            </Typography>
            <Badge variant="primary" text={`${stayPrice} €`} />
          </div>
        )
      }
    },
    {
      accessorKey: 'rowId',
      header: 'Lien vers la page',
      centerCell: true,
      width: '70px',
      cellClassName: 'px-0',
      cell: ({ row: { original: record } }) =>
        conditionalRender(
          !!record.productPageUrl,
          <a
            href={record.productPageUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            <GlobeIcon className="w-6 h-6" color={tailwindColors.blue.DEFAULT} />
          </a>
        )
    }
  ]

  return (
    <div>
      <div className="flex items-center justify-between">
        <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
          Filtrer :
        </Typography>
        <Button
          variant="destructive"
          size="small"
          icon={ResetTwoIcon}
          label="Réinitialiser"
          type="button"
          onClick={() => setFilter({})}
        />
      </div>
      <div className="h-3" />
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-3 gap-3">
          <SelectInput<number>
            label={'Âge'}
            options={childAges.map((age) => ({ label: age.label, value: age.value }))}
            selectedOption={conditionalRender(!!filter.age, {
              label: childAges.find((age) => age.value === filter.age)?.label ?? '',
              value: filter.age!
            })}
            onChange={(age) => setFilter({ ...filter, age })}
          />
          <SelectInput<number>
            label={'Pays'}
            options={countries?.data.map((country) => ({
              label: country.name,
              value: country.id
            }))}
            selectedOption={conditionalRender(!!filter.countryId, {
              label: countries?.data.find((c) => c.id === filter.countryId)?.name ?? '',
              value: filter.countryId!
            })}
            onChange={(countryId) => setFilter({ ...filter, countryId })}
          />
          <MultiSelectInput
            label={'Durée en semaines'}
            variant={'warning'}
            options={OTO_INDIVIDUAL_NUMBER_OF_WEEKS.map((weeks) => ({
              label: getOtoIndividualWeeksLabel(weeks),
              value: weeks
            }))}
            selectedOptions={filter.numberOfWeeks?.map((weeks) => ({
              label: getOtoIndividualWeeksLabel(weeks),
              value: weeks
            }))}
            onChange={(numberOfWeeks) => setFilter({ ...filter, numberOfWeeks })}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <MultiSelectInput
            label={"# d'h de cours/sem"}
            variant={'primary'}
            options={OTO_INDIVIDUAL_NUMBER_OF_HOURS.map((hours) => ({
              label: `${hours}h`,
              value: hours
            }))}
            selectedOptions={filter.numberOfHours?.map((hours) => ({
              label: `${hours}h`,
              value: hours
            }))}
            onChange={(numberOfHours) => setFilter({ ...filter, numberOfHours })}
          />
          <SelectInput<number>
            label={'Région du séjour'}
            options={regionOptions.map((place) => ({ label: place.name, value: place.id }))}
            selectedOption={conditionalRender(!!filter.journeyPlaceId, {
              label: regionOptions.find((place) => place.id === filter.journeyPlaceId)?.name ?? '',
              value: filter.journeyPlaceId!
            })}
            onChange={(journeyPlaceId) =>
              setFilter({ ...filter, journeyPlaceId, arrivalPlaceId: undefined })
            }
          />
          <SelectInput<number>
            label={"Lieu d'arrivée"}
            options={arrivalPlaceOptions.map((place) => ({
              label: place.title,
              value: place.id
            }))}
            selectedOption={conditionalRender(!!filter.arrivalPlaceId, {
              label:
                arrivalPlaceOptions.find((place) => place.id === filter.arrivalPlaceId)?.title ??
                '',
              value: filter.arrivalPlaceId!
            })}
            onChange={(arrivalPlaceId) => setFilter({ ...filter, arrivalPlaceId })}
          />
        </div>
      </div>
      <div className="h-4" />
      <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
        Produit OTO Indiv
      </Typography>
      <div className="h-2" />
      {isProductsPending ? (
        <Loader center />
      ) : isProductsError ? (
        <Typography variant="tag" color={tailwindColors.secondaryRed.DEFAULT}>
          Les séjours n’ont pas pu être chargés. Veuillez réessayer.
        </Typography>
      ) : (
        <DataTable<OtoIndividualProductRow>
          data={rows}
          paginated={false}
          manualSorting={false}
          mode="normal"
          columns={columns}
          rowCount={rows.length}
        />
      )}
      <div className="h-6" />
      <div className="flex w-full justify-end mb-4">
        <Button
          type="button"
          variant="primary"
          disabled={!selectedRowId}
          onClick={onSubmit}
          label="Suivant"
        />
      </div>
    </div>
  )
}

export default OtoIndividualProductListSelection
